<template>
  <CCard class="revenue-card border-0 shadow-sm">
    <CCardHeader class="d-flex justify-content-between align-items-center bg-white border-bottom-0 pt-3 px-3">
      <div>
        <h4 class="font-weight-bolder mb-2 d-flex align-items-center">
          <span
            class="mr-2"
            style="font-size: 1.5rem; filter: drop-shadow(0 2px 2px rgba(0,0,0,0.1));"
          >🚀</span>
          <span style="background: linear-gradient(45deg, #2563eb, #d946ef); -webkit-background-clip: text; -webkit-text-fill-color: transparent; letter-spacing: -1px;">
            Mission to $1M
          </span>
        </h4>
        <span
          class="badge badge-light text-uppercase font-weight-bold text-muted px-3 py-2 rounded-pill border"
          style="font-size: 0.75rem; letter-spacing: 0.5px; background-color: #f8fafc;"
        >
          🎯 Month Target: Apr 1, 2026
        </span>
      </div>
      <div class="d-flex align-items-center">
        <!-- Granularity Selector -->
        <CButtonGroup class="mr-3 shadow-sm rounded">
          <CButton
            v-for="option in granularityOptions"
            :key="option.column"
            size="sm"
            :color="selectedGranularity === option.column ? 'primary' : 'light'"
            class="font-weight-bold px-3"
            @click="changeGranularity(option.column)"
          >
            {{ option.label }}
          </CButton>
        </CButtonGroup>
      </div>
    </CCardHeader>
    <CCardBody class="px-3 pb-3">
      <!-- Mission Progress -->
      <div
        class="mission-progress mb-4 p-3 rounded"
        style="background: #f8f9fa;"
      >
        <div class="d-flex justify-content-between align-items-end mb-2">
          <div>
            <span class="text-uppercase font-weight-bold text-muted small">Current Best {{ currentPeriodLabel }}</span>
            <div class="d-flex align-items-baseline">
              <div
                class="h5 mb-0 text-primary font-weight-bold mr-2"
                style="color: #3399ff !important;"
              >
                {{ formatCurrencyCompact(maxRevenue) }}
              </div>
              <small class="font-weight-bold text-muted">({{ progressPercentage.toFixed(1) }}% achieved)</small>
            </div>
          </div>
          <div class="text-right">
            <span class="text-uppercase font-weight-bold text-muted small">Target Goal</span>
            <div
              class="h5 mb-0 font-weight-bold"
              style="color: #2eb85c;"
            >
              {{ formatCurrencyCompact(targetRevenue) }}
            </div>
          </div>
        </div>
        <div
          class="progress rounded-pill shadow-sm"
          style="height: 0.4rem; background-color: #e2e6ea;"
        >
          <div 
            class="progress-bar bg-primary" 
            role="progressbar" 
            :style="{ width: `${progressPercentage}%` }" 
            :aria-valuenow="progressPercentage" 
            aria-valuemin="0" 
            aria-valuemax="100"
          />
        </div>
        <!-- Custom injection for exact color override if 'primary' isn't exact enough -->
        <component :is="'style'">
          .progress-bar.bg-primary { background-color: #3399ff !important; }
        </component>
      </div>

      <!-- Chart -->
      <div v-if="!isLoading && chartData.labels.length > 0">
        <CChartBar
          :datasets="chartData.datasets"
          :labels="chartData.labels"
          :options="chartOptions"
          style="height: 300px;"
        />
      </div>
      <div
        v-else-if="isLoading"
        class="text-center py-5"
      >
        <CSpinner
          color="primary"
          grow
        />
        <p class="mt-2 text-muted font-weight-bold">
          Calculating trajectory...
        </p>
      </div>
      <div
        v-else
        class="text-center py-5 text-muted"
      >
        <p>No mission data available</p>
      </div>
    </CCardBody>
  </CCard>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { CChartBar } from '@coreui/vue-chartjs'
import moment from 'moment'

export default {
  name: 'RevenueChart',
  components: {
    CChartBar
  },
  props: {
    enableMockData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedGranularity: 'datesByYearMonth', // Default to Monthly
      granularityOptions: [
        { column: 'datesByYearMonthDay', label: 'Daily' },
        { column: 'datesByYearMonthWeek', label: 'Weekly' },
        { column: 'datesByYearMonth', label: 'Monthly' }
      ],
      mockDataPoints: [],
      birthdayIcon: 'crossRot'
    }
  },
  computed: {
    ...mapGetters('insights', {
      apiDataPoints: 'revenueChartData',
      summary: 'revenueChartSummary',
      isLoading: 'isRevenueChartLoading'
    }),

    dataPoints() {
      const points = this.enableMockData ? this.mockDataPoints : this.apiDataPoints
      if (!points) {
        return []
      }
      
      // Filter out summary records which typically lack a specific date/period
      return points.filter(p => p.date || p.period)
    },

    currentPeriodLabel() {
      switch (this.selectedGranularity) {
        case 'datesByYearMonthDay': return 'Daily'
        case 'datesByYearMonthWeek': return 'Weekly'
        case 'datesByYearMonth': default: return 'Monthly'
      }
    },

    targetRevenue() {
      // Mission Target is $1M Monthly
      const monthlyTarget = 1000000
      
      if (this.selectedGranularity === 'datesByYearMonthDay') {
        return monthlyTarget / 30.4
      }

      if (this.selectedGranularity === 'datesByYearMonthWeek') {
        return monthlyTarget / 4.33
      }

      return monthlyTarget
    },

    maxRevenue() {
      if (!this.dataPoints || this.dataPoints.length === 0) {
        return 0
      }

      // Return the actual max revenue achieved, defaulting to 0 if no positive values
      const values = this.dataPoints.map(dp => dp.conversionAmount)
      return Math.max(0, ...values)
    },

    progressPercentage() {
      const pct = (this.maxRevenue / this.targetRevenue) * 100
      return Math.min(pct, 100)
    },

    chartData() {
      if (!this.dataPoints || this.dataPoints.length === 0) {
        return { labels: [], datasets: [] }
      }

      // Sort data points by period
      const sorted = [...this.dataPoints].sort((a, b) =>
        new Date(this.getPeriodFromRecord(a)) - new Date(this.getPeriodFromRecord(b))
      )

      let displayData = []
      let futureLabels = []
      let futureTargetData = []
      const futurePointStyles = []
      const futurePointRadiuses = []
      const targetDate = moment('2026-04-01') // Define targetDate here for broader scope

      // --- Daily View: Show CURRENT MONTH only ---
      if (this.selectedGranularity === 'datesByYearMonthDay') {
         const startOfMonth = moment().startOf('month')
         const endOfMonth = moment().endOf('month')
         
         // Filter actuals to this month
         displayData = sorted.filter(dp => {
             const d = moment(this.getPeriodFromRecord(dp))
             return d.isSameOrAfter(startOfMonth, 'day') && d.isSameOrBefore(endOfMonth, 'day')
         })

         // Generate labels for every day of the month
         const iterDate = startOfMonth.clone()
         while (iterDate.isSameOrBefore(endOfMonth, 'day')) {
             futureLabels.push(this.formatPeriodLabel(iterDate))
             futureTargetData.push(this.calculateTrend(iterDate))
             
             // Check birthday icon
             const isTarget = iterDate.isSame(targetDate, 'day')
             futurePointStyles.push(isTarget ? this.birthdayIcon : 'crossRot')
             futurePointRadiuses.push(isTarget ? 10 : 4)

             iterDate.add(1, 'days')
         }
         
         // Map Actuals to the Day index
         // Create an array of nulls, then fill in known values
         const revenueDataFilled = new Array(futureLabels.length).fill(null)
         
         displayData.forEach(dp => {
             const d = moment(this.getPeriodFromRecord(dp))
             const dayIndex = d.date() - 1 // 1-based to 0-based
             if (dayIndex >= 0 && dayIndex < revenueDataFilled.length) {
                 revenueDataFilled[dayIndex] = dp.conversionAmount
             }
         })

         return {
            labels: futureLabels,
            datasets: [
                {
                    label: 'Revenue',
                    backgroundColor: '#3399ff',
                    hoverBackgroundColor: '#2075c2',
                    data: revenueDataFilled,
                    barPercentage: 0.6,
                    categoryPercentage: 0.8,
                    order: 2,
                    borderWidth: 1,
                    borderColor: '#3399ff'
                },
                {
                    label: 'Target',
                    type: 'line',
                    borderColor: '#2eb85c',
                    backgroundColor: 'rgba(46, 184, 92, 0.1)',
                    pointBackgroundColor: '#2eb85c',
                    borderWidth: 3,
                    borderDash: [5, 5],
                    fill: false,
                    data: futureTargetData,
                    pointRadius: futurePointRadiuses,
                    pointHoverRadius: 6,
                    pointStyle: futurePointStyles,
                    order: 1,
                    tension: 0
                }
            ]
         }
      }

      // --- Weekly/Monthly View: Show Last 6 Periods + Future Trend ---
      
      // Take last 6 periods from actuals
      displayData = sorted.slice(-6)

      // Calculate future periods up to April 1, 2026
      const lastRecord = displayData.length > 0 ? displayData[displayData.length - 1] : null
      const lastDate = lastRecord 
        ? moment.utc(this.getPeriodFromRecord(lastRecord)).endOf(
            this.selectedGranularity === 'datesByYearMonth' ? 'month' : 
            this.selectedGranularity === 'datesByYearMonthWeek' ? 'week' : 'day'
          )
        : moment().utc()

      
      // Determine step based on granularity
      let stepUnit = 'months'
      if (this.selectedGranularity === 'datesByYearMonthWeek') {
        stepUnit = 'weeks'
      }

      // Start from next period
      let nextDate = lastDate.clone().add(1, stepUnit)

      // Loop until hitting or passing target date
      while (nextDate.isBefore(targetDate) || nextDate.isSame(targetDate, stepUnit)) {
        const label = this.formatPeriodLabel(nextDate)
        
        // Prevent immediate duplicate of the last actual label
        const lastActualLabel = displayData.length > 0 
           ? this.formatPeriodLabel(this.getPeriodFromRecord(displayData[displayData.length - 1])) 
           : ''
           
        if (futureLabels.length === 0 && label === lastActualLabel) {
           nextDate.add(1, stepUnit)
           continue
        }

        futureLabels.push(label)
        futureTargetData.push(this.calculateTrend(nextDate))
        
        const isTarget = nextDate.isSame(targetDate, stepUnit)
        futurePointStyles.push(isTarget ? this.birthdayIcon : 'crossRot')
        futurePointRadiuses.push(isTarget ? 10 : 4)

        nextDate.add(1, stepUnit)
        // Safety break
        if (futureLabels.length > 500) {
          break
        }
      }

      // Combine Labels
      const labels = [
        ...displayData.map(dp => this.formatPeriodLabel(this.getPeriodFromRecord(dp))),
        ...futureLabels
      ]
      
      // Combine Revenue (Active + Nulls)
      const revenueData = [
        ...displayData.map(dp => dp.conversionAmount),
        ...new Array(futureLabels.length).fill(null)
      ]

      // Combine Remaining (Target - Actual) to create a full pillar
      const remainingData = [
         ...displayData.map(dp => {
            const val = dp.conversionAmount
            const textVal = Math.max(0, this.targetRevenue - val)
            return textVal
         }),
         ...new Array(futureLabels.length).fill(null)
      ]

      // Combine Target Trend (Active + Future)
      const targetData = [
         ...displayData.map(dp => {
            const periodDate = this.getPeriodFromRecord(dp)
            return this.calculateTrend(periodDate)
         }),
         ...futureTargetData
      ]

      return {
        labels,
        datasets: [
          {
            label: 'Actual',
            backgroundColor: '#3399ff', // Brand Blue
            hoverBackgroundColor: '#2075c2',
            data: revenueData,
            barPercentage: 0.8, // Wider bars
            categoryPercentage: 0.9,
            order: 2,
            borderWidth: 2, // Thicker border
            borderColor: '#3399ff',
            stack: 'revenue',
            minBarLength: 4 // Ensure at least 4px height for non-zero values
          },
          {
            label: 'Remaining to Target',
            backgroundColor: '#f8f9fa', // Very Light Gray
            hoverBackgroundColor: '#f0f3f5',
            data: remainingData,
            barPercentage: 0.8,
            categoryPercentage: 0.9,
            order: 3,
            borderWidth: 0,
            stack: 'revenue' // Stack with Actual
          },
          {
            label: 'Trend',
            type: 'line',
            borderColor: '#2eb85c', // Brand Green
            backgroundColor: 'rgba(46, 184, 92, 0.1)',
            pointBackgroundColor: '#2eb85c',
            borderWidth: 3,
            borderDash: [5, 5],
            fill: false,
            data: targetData,
            pointRadius: [
              ...displayData.map(dp => {
                  const d = moment(this.getPeriodFromRecord(dp))
                  return d.isSame(targetDate, stepUnit) ? 10 : 4
              }),
              ...futurePointRadiuses
            ],
            pointHoverRadius: 6,
            pointStyle: [
              ...displayData.map(dp => {
                  const d = moment(this.getPeriodFromRecord(dp))
                  return d.isSame(targetDate, stepUnit) ? this.birthdayIcon : 'crossRot'
              }),
              ...futurePointStyles
            ],
            order: 1,
            tension: 0
          }
        ]
      }
    },

    chartOptions() {
      return {
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            boxWidth: 10,
            fontColor: '#5c6873',
            fontStyle: 'bold',
            filter: (item) => item.text !== 'Remaining to Target' // Hide Remaining from legend
          }
        },
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: {
              drawOnChartArea: false,
              display: false
            },
            ticks: {
              autoSkip: false,
              maxRotation: 0,
              callback: function(value, index, values) {
                const label = value
                if (values.length <= 12) {
                    return label
                }

                if (index === values.length - 1) {
                    return label
                } // Always show last
                
                const gap = Math.ceil(values.length / 8)
                if (index % gap === 0) {
                    return label
                }
                
                return null // Hide others
              }
            }
          }],
          yAxes: [{
            position: 'right', // Put Y axis on right like a stock chart
            stacked: true,
            gridLines: {
              drawBorder: false,
              borderDash: [2, 4],
              color: '#ced4da'
            },
            ticks: {
              callback: (value) => this.formatCurrencyCompact(value),
              beginAtZero: true,
              padding: 10,
              suggestedMax: this.targetRevenue * 1.05 // Ensure target is visible with slight padding
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0,0,0,0.8)',
          titleFontColor: '#fff',
          bodyFontColor: '#fff',
          cornerRadius: 4,
          filter: (item) => item.datasetIndex !== 1, // Hide Remaining tooltip
          callbacks: {
            label: (tooltipItem, data) => {
              const label = data.datasets[tooltipItem.datasetIndex].label || ''
              const value = tooltipItem.yLabel
              
              // For Trend line, show value normally
              if (data.datasets[tooltipItem.datasetIndex].type === 'line') {
                   return `${label}: ${this.formatCurrencyCompact(value)}`
              }
              
              // For Bar (Actual), show absolute value (since yLabel might be stacked position in some versions, but usually yLabel is absolute value in v2 stacked or accumulated? 
              // In Chart.js v2 stacked, yLabel is the cumulative value. Wait. 
              // Actually tooltipItem.yLabel is usually the value of the datapoint unless implied.
              // Let's safe bet: access raw data.
              const dataset = data.datasets[tooltipItem.datasetIndex]
              const rawData = dataset.data[tooltipItem.index]
              
              return `${label}: $${this.formatNumber(rawData)}`
            }
          }
        }
      }
    }
  },
  async mounted() {
    this.birthdayIcon = this.createEmojiIcon('🎂')
    await this.loadRevenueChart()
  },
  methods: {
    ...mapActions('insights', ['fetchRevenueChart']),

    async loadRevenueChart() {
      if (this.enableMockData) {
        this.generateMockData()
        return
      }

      // Backend always returns last 6 months
      await this.fetchRevenueChart({
        granularityColumn: this.selectedGranularity
      })
    },

    async changeGranularity(granularityColumn) {
      this.selectedGranularity = granularityColumn
      await this.loadRevenueChart()
    },

    generateMockData() {
      const mockData = []
      const count = 6

      // Target Dates for Calculation
      const targetDate = moment('2026-04-01')
      const startDate = moment('2025-10-01') // Start of Q4 2025

      const startVal = 200000
      const endVal = 1000000

      for (let i = count; i >= 0; i--) {
        let date
        if (this.selectedGranularity === 'datesByYearMonthDay') {
          date = moment().subtract(i, 'days')
        } else if (this.selectedGranularity === 'datesByYearMonthWeek') {
          date = moment().subtract(i, 'weeks')
        } else {
          date = moment().subtract(i, 'months')
        }

        // Calculate a realistic value based on the trend for this specific granularity
        const trendValue = this.calculateTrend(date)
        
        // Add some noise: +/- 15%
        const noise = 1 + ((Math.random() * 0.3) - 0.15) 
        
        // Simulate slightly underperforming the target
        const actualTotal = Math.floor(trendValue * noise * 0.85)

        mockData.push({
          conversionAmount: actualTotal,
          date: date.toISOString()
        })
      }
      
      this.mockDataPoints = mockData
    },

    getPeriodFromRecord(record) {
      // Prioritize 'date' (future API), fallback to 'period' (current API not yet redeployed)
      return record.date || record.period
    },

    formatPeriodLabel(period) {
      const date = moment.utc(period)
      switch (this.selectedGranularity) {
        case 'datesByYearMonthDay': return date.format('D MMM')
        case 'datesByYearMonthWeek': return 'W ' + date.format('D MMM')
        case 'datesByYearMonth': default: return date.format('MMM YYYY')
      }
    },

    calculateTrend(period) {
      const date = moment(period)
      const targetDate = moment('2026-04-01')
      const startDate = moment('2025-10-01') // Start of Q4 2025
      
      const monthlyTarget = 1000000
      let startVal = 0
      let endVal = 0

      // Calculate start/end values based on granularity targeting the Monthly Goal
      if (this.selectedGranularity === 'datesByYearMonthDay') {
        startVal = (monthlyTarget * 0.1) / 30.4 // Start at 10% of target
        endVal = monthlyTarget / 30.4
      } else if (this.selectedGranularity === 'datesByYearMonthWeek') {
        startVal = (monthlyTarget * 0.1) / 4.33
        endVal = monthlyTarget / 4.33
      } else {
        // Monthly
        startVal = monthlyTarget * 0.1
        endVal = monthlyTarget
      }

      const totalDuration = targetDate.diff(startDate, 'days')
      const currentElapsed = date.diff(startDate, 'days')
      
      let progress = currentElapsed / totalDuration
      
      // Linear extrapolation 
      let idealValue = startVal + ((endVal - startVal) * progress)

      // Clamp to 0
      if (idealValue < 0) {
        idealValue = 0
      }

      return Math.floor(idealValue)
    },

    formatNumber(value) {
      if (value === null || value === undefined) {
        return '0'
      }

      return Number(value).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },

    formatCurrencyCompact(value) {
      if (value === 0) {
        return '$0'
      }
      
      if (value >= 1000000) {
        return '$' + (value / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      }

      if (value >= 1000) {
        return '$' + (value / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
      }

      return '$' + value
    },

    createEmojiIcon(emoji) {
      const canvas = document.createElement('canvas')
      canvas.width = 40
      canvas.height = 40
      const ctx = canvas.getContext('2d')
      ctx.font = '30px serif'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(emoji, 20, 20)
      return canvas
    }
  }
}
</script>

<style lang="scss" scoped>
.revenue-card {
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
  }
}
</style>
